import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ICryptoService } from '../../../services/crypto.service';
import { IEventBus } from '../../../services/eventBus.service';
import { ICurrentWorkspaceRoleProps } from '../components/CurrentWorkspaceRole';
import { IUnlockModalProps } from '../components/UnlockWorkspaceModal';
import { WorkspaceDecryptionFailedEvent } from '../events/workspaceDecryptionFailedEvent';
import { IWorkspaceService } from '../workspaces.service';

interface IProps {
  children(unlockModalProps: IUnlockModalProps, currentWorkspaceRoleProps: ICurrentWorkspaceRoleProps): ReactNode;
}

export const CurrentWorkspaceContainer = observer((props: IProps): ReactElement => {
  const workspaceService = useInjection(IWorkspaceService.$);
  const cryptoService = useInjection(ICryptoService.$);
  const eventBus = useInjection(IEventBus.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const [desiredWorkspaceId, setDesiredWorkspaceId] = useState<string | undefined>(undefined);
  const [isUnlockModalShown, setIsUnlockModalShown] = useState(false);
  const [expectedChecksum, setExpectedChecksum] = useState<string | undefined>(undefined);
  const [wrongKeyFile, setWrongKeyFile] = useState(false);
  const [workspaceName, setWorkspaceName] = useState<string | undefined>(undefined);

  const checkWorkspaceEncryption = async (): Promise<void> => {
    const workspace = currentWorkspaceStore.currentWorkspace;
    if (workspace && workspace.encryption.encrypted && !(await cryptoService.checkKey(workspace.id, workspace.id))) {
      tryOpenWorkspace(workspace.id);
    }
  };

  const checkPropsWorkspace = async (): Promise<void> => {
    const workspace = currentWorkspaceStore.currentWorkspace;
    if (desiredWorkspaceId === workspace.id) {
      return;
    }
    if (workspace && workspace.encryption.encrypted && !(await cryptoService.checkKey(workspace.id, workspace.id)) && !desiredWorkspaceId) {
      tryOpenWorkspace(workspace.id);
    } else {
      setIsUnlockModalShown(false);
      setDesiredWorkspaceId(undefined);
      setExpectedChecksum(undefined);
      setWrongKeyFile(false);
    }
  };

  useEffect(() => {
    void checkWorkspaceEncryption();
  }, [currentWorkspaceStore.currentWorkspace]);

  useEffect(() => {
    void checkPropsWorkspace();
  }, [currentWorkspaceStore.currentWorkspace, desiredWorkspaceId]);

  const tryOpenWorkspace = (desiredWorkspaceId: string): void => {
    const desiredWorkspace = workspaceService.store.workspaces.find((w) => w.id === desiredWorkspaceId);
    if (desiredWorkspace && desiredWorkspace.encryption.encrypted && !cryptoService.hasKey(desiredWorkspaceId)) {
      showUnlockModal(desiredWorkspaceId, desiredWorkspace.encryption.checksum);
      return;
    }
  };

  const tryUnlockWorkspace = async (desiredWorkspaceId: string, key?: string): Promise<void> => {
    if (expectedChecksum && key !== undefined) {
      if (!(await cryptoService.loadKey(desiredWorkspaceId, key, desiredWorkspaceId, expectedChecksum))) {
        setWrongKeyFile(true);
        return;
      }
    }

    setIsUnlockModalShown(false);
    setDesiredWorkspaceId(undefined);
    setExpectedChecksum(undefined);
    setWrongKeyFile(false);
  };

  const showUnlockModal = (desiredWorkspaceId: string, expectedChecksum: string): void => {
    const workspaceName = workspaceService.store.workspaces.find((w) => w.id === desiredWorkspaceId)?.name;
    setDesiredWorkspaceId(desiredWorkspaceId);
    setExpectedChecksum(expectedChecksum);
    setWorkspaceName(workspaceName);
    setIsUnlockModalShown(true);
  };

  const onFilesDrop = async (files: File[]): Promise<void> => {
    if (desiredWorkspaceId) {
      await tryUnlockWorkspace(desiredWorkspaceId, await files[0].text());
    }
  };

  const onWrongFilesDrop = (): void => {
    setWrongKeyFile(true);
  };

  const onCancel = (): void => {
    eventBus.sendEvent(new WorkspaceDecryptionFailedEvent());
  };

  return (
    <div className='top-navigation-workspaces-container'>
      {props.children(
        {
          onCancel,
          onFilesDrop,
          onWrongFilesDrop,
          showUnlockModal: isUnlockModalShown,
          wrongKeyFile,
          workspaceName,
        },
        {
          role: currentWorkspaceStore.currentWorkspace.role,
        }
      )}
    </div>
  );
});
